/**
 * Preferences.js
 * Talkroute
 *
 * Created by Vel Genov on 12/14/18
 * Copyright (c) 2018 Talkroute. All right reserved.
 */

import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { CSSTransition } from "react-transition-group";
import {
  fetchPhoneNumbers,
  togglePreferencesWindow,
} from "../../actions/preferencesActions";
import uiString from "../../constants/uiString";
import { useAppSelector } from "../../helpers/hooks/useAppSelector.hook";
import { companyItems, yourProfileItems, phoneSystemItems, NavigationItems } from "../../constants/navItems";
import PreferencesNavigation from "./PreferencesNavigation";
import ShowContent from "./ShowContent";
import { getFormattedDateAndTime } from "@helpers/functions/dateFormatter";
import { ComponentDimensions, isIOS } from "@constants/appConstants";
import { makeStyles, Theme } from "@material-ui/core";
import appConfig from "../../config";
import { useHistory, useLocation } from "react-router-dom";
import _ from "lodash";
import CallerID from "./CallerID";
import { hoursToSeconds } from "date-fns/esm";
import routes from "@constants/routes";


type IProps = {
  openPreferences: boolean,
  fullSizePreferences: boolean
}

/**
 * A page to display customizable preferences
 */
const Preferences: React.FC<IProps> = (props: IProps) => {

  // merge navItems.ts objects into single object
  const navItems = {
    ...companyItems,
    ...yourProfileItems,
    ...phoneSystemItems,
  }

  const lastUpdateCheckTimestamp = useAppSelector(
    (state) => state.preferences.lastUpdateCheckTimestamp
  );
  
  // set the default active item to Caller ID
  const [activeItem, setActiveItem] = useState(
    yourProfileItems.CALLER_ID.value
  );

  const dispatch = useDispatch();
  const classes = useStyles(props);
  const history = useHistory();
  const location = useLocation();
   // the route (url) 
  const pathname = location.pathname;
  
  const setDefaultRoute = () => {
    if(pathname === routes.KEYPAD && activeItem === yourProfileItems.CALLER_ID.value) {
      history.push(String(yourProfileItems.CALLER_ID.route));
    }
  }

  const handleRoute = () => {
    
    // get the key which value matches the pathname (route)
    const navItemKey = Object.keys(navItems).find(k => navItems[k].route === pathname);

    if (navItemKey) {
      // if we have a match, set it to be the active item
      setActiveItem(navItems[navItemKey].value);
    }

  }

  /**
   * Show the last time the application has changed for updates
   */
  const showLastUpdateTimestamp = () => {
    let updateText = uiString.preferences.NEVER;

    if (lastUpdateCheckTimestamp) {
      const date = moment.unix(lastUpdateCheckTimestamp);
      const dateFormatted = getFormattedDateAndTime(date);

      if (dateFormatted) {
        updateText = dateFormatted;
      }
    }

    return `${uiString.preferences.LAST_UPDATE_CHECK}: ${updateText}`;
  };

  /**
   * Navigate to the preferences sub page
   */
  const handleItemClick = (name: string) => {
    // this function works in the same way handleRoute() does
    const navItemKey = Object.keys(navItems).find(k=>navItems[k].value===name);
    if(navItemKey){
      setActiveItem(navItems[navItemKey].value);
        history.push(String(navItems[navItemKey].route))
    }
  
  };

  const dispatchFetchedPhoneNumbers = () => {
    dispatch(fetchPhoneNumbers());
  };

  /**
   * Update the preferences window visibility state
   *
   * @param isVisible indiacates wheather the preferences window is visible
   */
  const updateVisibility = (isVisible: boolean) => {
    dispatch(togglePreferencesWindow(isVisible));
  };


  /**
   * A lifecycle method called to do setup when the component is mounted
   */
  useEffect(() => {
    updateVisibility(true);
    dispatchFetchedPhoneNumbers();

    return () => updateVisibility(false);
  }, []);

  useEffect(() => {
    setDefaultRoute();
  }, []);

  useEffect(() => {
      handleRoute();
  }, [pathname]);

  return (
    <div
      style={{
        display: "flex",
      }}
    >
      <div
        className={classes.navWrapper}
        style={{
          minHeight: `calc(100vh - ${
            ComponentDimensions.SETTINGS_AND_PREFERENCES_HEADER_HEIGHT
          }px - ${
            appConfig.clientType === "desktop" && !isIOS
              ? ComponentDimensions.DESKTOP_TOOLBAR_HEIGHT
              : 0
          }px)`,
        }}
      >
        {/* This column contains the navigation of the preferences */}
        <PreferencesNavigation
          activeItem={activeItem}
          handleItemClick={handleItemClick}
        />
        {appConfig.clientType !== "desktop" && (
          <p className={classes.versionIdentifier}>
            {`${uiString.preferences.VERSION} ${appConfig.version}`}
          </p>
        )}
      </div>
      <div className={classes.preferences}>
        <CSSTransition
          key={activeItem}
          in
          appear
          timeout={300}
          classNames="fade"
        >
          {/* This column contains the main content of the preferences */}
          <ShowContent activeItem={activeItem} />
        </CSSTransition>
      </div>
    </div>
  );
};

const useStyles = makeStyles<Theme, IProps>((theme) => ({
  preferences: {
    flex: "1",
    padding: "20px 60px",
    paddingRight: 90,
    paddingTop: ComponentDimensions.PREFERENCES_PAGE_CONTENT_TOP_PADDING,
 //   marginLeft: ComponentDimensions.PREFERENCES_NAVIGATION_WIDTH,
    marginLeft: ({fullSizePreferences}) => fullSizePreferences === true ? "0" : ComponentDimensions.PREFERENCES_NAVIGATION_WIDTH,
    paddingLeft: ({fullSizePreferences}) => fullSizePreferences === true ? "90px" : "60px",
    background: ({fullSizePreferences}) => fullSizePreferences === true ? "#fff" : "transparent",
    zIndex: ({fullSizePreferences}) => fullSizePreferences === true ? 2 : 1,
    transition: theme.transitions.create(['margin-left', 'zIndex', 'background', 'padding-left'], {duration: "0.5s"}),
    marginTop: ComponentDimensions.SETTINGS_AND_PREFERENCES_HEADER_HEIGHT,
    height: "100vh",
    maxHeight: `calc(100vh - ${
      ComponentDimensions.SETTINGS_AND_PREFERENCES_HEADER_HEIGHT
    }px - ${
      appConfig.clientType === "desktop"
        ? ComponentDimensions.DESKTOP_TOOLBAR_HEIGHT
        : 0
    }px)`,
    overflow: "auto",
    [theme.breakpoints.down(1100)]: {
      padding: "20px 40px",
      paddingRight: 60,
      paddingTop: ComponentDimensions.PREFERENCES_PAGE_CONTENT_TOP_PADDING,
    },
    [theme.breakpoints.down("sm")]: {
      padding: "20px 20px",
      paddingRight: 10,
      paddingTop: ComponentDimensions.PREFERENCES_PAGE_CONTENT_TOP_PADDING,
    },
  },
  navWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    width: ComponentDimensions.PREFERENCES_NAVIGATION_WIDTH,
    maxHeight: `calc(100vh - ${ComponentDimensions.SETTINGS_AND_PREFERENCES_HEADER_HEIGHT}px - ${
      appConfig.clientType === "desktop"
        ? ComponentDimensions.DESKTOP_TOOLBAR_HEIGHT
        : 0
    }px)`,
    height: "100vh",
    position: "fixed",
    inset: 0,
    padding: "20px 10px",
    paddingTop: ComponentDimensions.PREFERENCES_PAGE_CONTENT_TOP_PADDING,
    background: theme.palette.common.whiteTan,
    marginTop: ComponentDimensions.SETTINGS_AND_PREFERENCES_HEADER_HEIGHT,
    overflowY: "auto",
  },
  versionIdentifier: {
    color: "var(--color-dark-grey)",
  },
}));

export default Preferences;
